export const usePricingContent = () => {
  return [
    {
      title: 'Platform access',
      isOpen: true,
      features: [
        {
          title: 'Number of reporters',
          subtitle: 'The number of people who can submit reports',
          description: 'The number of employees or stakeholders who can submit reports or disclosures through your platform.',
          core: 'Unlimited',
          enterprise: 'Unlimited'
        },
        {
          title: 'Internal user seats',
          subtitle: 'Control access to the administrative dashboard',
          description: 'The number of authorised staff members who can access the administrative dashboard to receive, manage, and respond to disclosures.',
          core: 'Unlimited',
          enterprise: 'Unlimited'
        },
        {
          title: 'iOS and Android app',
          subtitle: 'Access platform on mobile devices',
          description: 'Access the platform through secure mobile apps, enabling convenient and discrete reporting.',
          core: true,
          enterprise: true
        }
      ]
    },
    {
      title: 'Receiving reports',
      isOpen: true,
      features: [
        {
          title: 'Reporting pathways (workflows)',
          subtitle: 'Build reporting channels with custom workflows',
          description: 'The number of distinct reporting channels, each with its own customised workflow, automated responses, and handling procedures for different types of disclosures.',
          core: 'Configurable',
          enterprise: 'Custom'
        },
        {
          title: 'Anonymous reporting',
          subtitle: 'Enable secure anonymous disclosures',
          description: 'Submit sensitive disclosures through a secure portal the guarantees reporter anonymity, enabling effective case management and resolution.',
          core: true,
          enterprise: true
        },
        {
          title: '2-way encrypted chat',
          subtitle: 'Secure communication with reporters',
          description: 'Engage in secure, end-to-end encrypted conversations with reporters while maintaining their anonymity throughout the entire disclosure process.',
          core: true,
          enterprise: true
        }
      ]
    },
    {
      title: 'Case management tools',
      isOpen: true,
      features: [
        {
          title: 'Triage and case management',
          subtitle: 'Automated case prioritisation',
          description: 'Prioritise and route reports based on type and urgency, ensuring support, advice and attention while maintaining organised oversight.',
          core: true,
          enterprise: true
        },
        {
          title: 'Statuses and tags',
          subtitle: 'Define organisation-specific status labels',
          description: 'Use case statuses and specific labels allowing you to categorise reports for more granulated KPIs and analytics.',
          core: 'Standard',
          enterprise: 'Custom'
        },
        {
          title: 'Report export',
          subtitle: 'Generate comprehensive case reports',
          description: 'Generate comprehensive case reports in multiple formats - from standard summary exports to custom templates, to advanced analytics.',
          core: 'Standard',
          enterprise: 'Custom'
        },
        {
          title: 'Notifications and emails',
          subtitle: 'Customise alert settings and templates',
          description: 'Control when and how users receive platform alerts, with Standard offering basic notification templates, Personalised allowing custom messaging, and Advanced enabling complex notification rules and schedules.',
          core: 'Standard',
          enterprise: 'Custom'
        },
        {
          title: 'Secure mail',
          subtitle: 'Send encrypted messages',
          description: `Send messages via an encrypted channel to someone's email address while ensuring that data never leaves the platform.`,
          core: true,
          enterprise: true
        },
        {
          title: 'Sharing',
          subtitle: 'Control access to case information',
          description: 'Control and monitor access to sensitive case information across your organisation, with granular permissions and audit trails to maintain security and confidentiality.',
          core: true,
          enterprise: true
        },
        {
          title: 'Connect advisors and support securely',
          subtitle: 'Brief external parties in a secure, encrypted environment',
          description: 'Grant secure, controlled access to external parties (such as coaches, EAP, legal counsel, auditors or investigators) on specific reports, with customised permission settings and activity monitoring.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Automations and actions',
          subtitle: 'Configure automated workflows',
          description: `Set up automatic responses to specific triggers - from standard actions like case assignments, to advanced workflows with multiple conditions, to fully customisable automation sequences tailored to your organisation's needs.`,
          core: 'Standard',
          enterprise: 'Custom'
        },
        {
          title: 'Custom fields',
          subtitle: 'Create tailored input fields for reports',
          description: `Create tailored input fields and questions in your reporting forms to capture specific information that aligns with your organisation's case management requirements.`,
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Advanced form logic',
          subtitle: 'Build dynamic, adaptive forms',
          description: 'Create dynamic forms that adapt based on user inputs, with conditional logic and automated routing to streamline the reporting and case management process.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Reporter experience surveys',
          subtitle: 'Gather feedback on the reporter experience',
          description: 'Measure the reporter experience for support, advice and other problem-solving pathways.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Group profiles',
          subtitle: 'Enable team-based report management',
          description: 'Set up a team profile to receive reports with a choice of shared access or locked triage access.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Multiple chat groups',
          subtitle: 'Create separate discussion spaces',
          description: 'Create separate, secure discussion spaces within a single report where different stakeholders can collaborate.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Follow-up forms',
          subtitle: 'Automate obtaining further information with specific questions and reminders',
          description: 'Collect information required by legislation or company policy through follow-up questionnaires to meet business process requirements.',
          core: 'Add-on',
          enterprise: true
        }
      ]
    },
    {
      title: 'Analytics',
      isOpen: true,
      features: [
        {
          title: 'Analytics dashboards',
          subtitle: 'Monitor organisational health',
          description: 'Monitor organisational health through interactive dashboards that identify trends and emerging patterns in reporting data. From standard metrics to custom data combinations, gain actionable insights to proactively address workplace issues.',
          core: true,
          enterprise: true
        },
        {
          title: 'Search and filter',
          subtitle: 'Analyse de-identified data',
          description: 'Search and analyse de-identified data without ever worrying about exposure of confidential information or PII. Powerful search and filter functions make this easy.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Export to CSV',
          subtitle: 'Export data for further analysis',
          description: 'Easily export your visible data set to CSV format for further use.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Export to PDF',
          subtitle: 'Create snapshot reports',
          description: 'Export your charts and data display to PDF to capture a snapshot of your data.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Personalised data points and charts',
          subtitle: 'Build custom analytics',
          description: 'Build custom data sets, charts and trend analytics from the data you have in your Elker system. Save your favourite views and personalise these for reporting requirements.',
          core: 'Add-on',
          enterprise: true
        }
      ]
    },
    {
      title: 'Security',
      isOpen: true,
      features: [
        {
          title: 'ISO 27001 certified',
          subtitle: 'Information security management standards',
          description: 'Elker is ISO 27001 certified, demonstrating our commitment to maintaining the highest standards of information security management and data protection.',
          core: true,
          enterprise: true
        },
        {
          title: 'SOC2 attested',
          subtitle: 'Advanced security compliance',
          description: 'Elker meets rigorous SOC2 security, availability, and confidentiality standards, providing independent verification of our security controls and practices.',
          core: true,
          enterprise: true
        },
        {
          title: 'GDPR compliant',
          subtitle: 'EU data protection compliance',
          description: 'Full compliance with EU General Data Protection Regulation requirements, ensuring proper handling of personal data with features for data subject rights, consent management, and privacy protection.',
          core: true,
          enterprise: true
        },
        {
          title: 'Choose where your data is located',
          subtitle: 'By default, data is stored in Australia or the EU',
          description: false,
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Additional data jurisdictions',
          subtitle: 'Select data storage location',
          description: 'Select where your data is physically stored and processed to meet specific regulatory requirements or data sovereignty needs.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'User roles and policies',
          subtitle: 'Configure access permissions',
          description: 'Define access levels and permissions with standard preset roles, customisable role configurations, or advanced hierarchical permission structures.',
          core: 'Standard',
          enterprise: 'Custom'
        },
        {
          title: 'Single Sign On',
          subtitle: 'Enterprise authentication integration',
          description: `Enable seamless and secure platform access through your organisation's existing authentication system, simplifying user management and maintaining enterprise-grade security.`,
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Audit log',
          subtitle: 'Track system activities',
          description: 'Track all system activities from standard event logging to advanced audit trails with detailed forensics and compliance reporting capabilities.',
          core: 'Add-on',
          enterprise: 'Advanced'
        }
      ]
    },
    {
      title: 'Support',
      isOpen: true,
      features: [
        {
          title: 'Support plan',
          subtitle: 'Technical assistance options',
          description: 'Access technical assistance and platform guidance - from standard support with regular response times, to premium with priority service, to custom SLA with tailored support agreements meeting your specific needs.',
          core: true,
          enterprise: 'Custom SLA'
        },
        {
          title: 'Managed pathways',
          subtitle: 'Expert case management',
          description: `Triage, case management and resolution pathways such as investigation and mediation. Our network of partners will manage the process for you.`,
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Dedicated account manager',
          subtitle: 'Personalised platform support',
          description: 'Receive hands-on assistance with platform configuration, workflow optimisation and dashboard analytics from a dedicated Elker specialist. Useful for organisations unable to manage critical whistleblowing or public interest disclosures.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Policies and frameworks',
          subtitle: 'Access compliance templates',
          description: 'Access policy templates for workplace conduct, whistleblowing and privacy.',
          core: true,
          enterprise: true
        }
      ]
    },
    {
      title: 'Additional features',
      isOpen: true,
      features: [
        {
          title: 'Third-party integrations',
          subtitle: 'Connect with existing tools and your EAP',
          description: 'Connect Elker with your existing business tools and your employee assistance program - from standard integrations with common platforms to custom API solutions tailored to your specific technology stack.',
          core: 'Add-on',
          enterprise: 'Custom'
        },
        {
          title: 'API access',
          subtitle: 'Create custom integrations',
          description: `Access to Elker's full-featured API to create custom integrations with your existing tools.`,
          core: 'Add-on',
          enterprise: 'Custom'
        },
        {
          title: 'Culture surveys',
          subtitle: 'Assess organisational health',
          description: 'Proactively assess organisational culture through surveys, identifying potential issues before they escalate into reportable incidents. Generate data-driven insights to guide preventive measures and cultural improvements.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Languages',
          subtitle: 'Multi-language support',
          description: 'Support for multiple interface languages and auto-translation tools, ensuring accessibility for your entire global workforce.',
          core: 'Add-on',
          enterprise: 'Custom'
        },
        {
          title: 'Phone hotline',
          subtitle: 'Voice-based reporting option',
          description: 'Enable voice-based reporting through a dedicated phone line, offering an alternative channel while maintaining confidentiality and security.',
          core: 'Add-on',
          enterprise: true
        },
        {
          title: 'Training program',
          subtitle: 'Comprehensive user education',
          description: 'Access comprehensive onboarding and compliance training modules that educate users on reporting procedures, compliance requirements, and platform functionality, ensuring effective system adoption.',
          core: 'Add-on',
          enterprise: true
        }
      ]
    }
  ]
}
